import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { useGetOrderById } from "@/services";
import {
	ErrorBoundary,
	Icon,
	TabItem,
	Tabs,
	TabsList,
} from "@st-marketplace/ui";
import type { FC } from "react";
import {
	OrderDetailsHeader,
	OrderDetailsHistory,
	OrderDetailsInfo,
	OrderDetailsInfoSkeleton,
	OrderDetailsTransactions,
} from "./components";
import OrderDetailsPlan from "./components/plan/OrderDetailsPlan";
import OrderDetailsShadowPlan from "./components/plan/OrderDetailsShadowPlan";

interface OrderDetailsProps {
	orderId: string;
	tabValue: string;
	onTabChange: (value: string) => void;
}

const OrderDetails: FC<OrderDetailsProps> = ({
	orderId,
	tabValue,
	onTabChange,
}) => {
	const { getErrorInterface } = useApiError();

	const { t } = useTranslation();

	const {
		data: orderData,
		isLoading,
		error,
		refetch,
	} = useGetOrderById({
		path: { order_id: orderId },
	});

	// a customer-link order not claimed yet has no EA account, so nothing was delivered yet
	const isUnclaimed = orderData?.schema_type === "end_customer_unclaimed";
	const showTransactions =
		orderData?.schema_type !== "for_buyer" && !isUnclaimed;

	const showPlan =
		orderData && "can_view_plan" in orderData && orderData.can_view_plan;
	const showShadowPlan =
		orderData &&
		"can_view_shadow_plan" in orderData &&
		orderData.can_view_shadow_plan;

	return (
		<div className="flex min-w-0 flex-col gap-6">
			<ErrorBoundary
				isLoading={isLoading}
				error={error ? getErrorInterface(error) : undefined}
				onReset={refetch}
				skeleton={<OrderDetailsInfoSkeleton />}
			>
				{orderData ? (
					<>
						<OrderDetailsHeader order={orderData} />
						<div className="min-w-0 overflow-x-auto">
							<Tabs value={tabValue} onValueChange={onTabChange}>
								<TabsList>
									<TabItem value="0" Icon={<Icon name="information-line" />}>
										{t("orderInfo.tabInfo")}
									</TabItem>
									{showTransactions && (
										<TabItem value="1" Icon={<Icon name="exchange-line" />}>
											{t("orderInfo.tabTransactions")}
										</TabItem>
									)}
									{!isUnclaimed && (
										<TabItem value="2" Icon={<Icon name="file-list-3-line" />}>
											{t("orderInfo.tabHistory")}
										</TabItem>
									)}
									{showPlan && (
										<TabItem value="3" Icon={<Icon name="list-ordered" />}>
											{t("orderPlan.title")}
										</TabItem>
									)}
									{showShadowPlan && (
										<TabItem value="4" Icon={<Icon name="line-chart-line" />}>
											{t("orderPlanV2.title")}
										</TabItem>
									)}
								</TabsList>
							</Tabs>
						</div>
						{(tabValue === "0" ||
							(tabValue === "3" && !showPlan) ||
							(tabValue === "4" && !showShadowPlan)) && (
							<OrderDetailsInfo data={orderData} />
						)}
						{tabValue === "4" && showShadowPlan && (
							<OrderDetailsShadowPlan key={orderId} orderId={orderId} />
						)}
						{tabValue === "3" && showPlan && (
							<OrderDetailsPlan
								key={orderId}
								orderId={orderId}
								live={
									"status" in orderData && orderData.status === "PROCESSING"
								}
							/>
						)}
						{tabValue === "1" && showTransactions && (
							<OrderDetailsTransactions />
						)}
						{tabValue === "2" && "origin_instance_id" in orderData && (
							<OrderDetailsHistory instanceId={orderData.origin_instance_id} />
						)}
					</>
				) : null}
			</ErrorBoundary>
		</div>
	);
};

export default OrderDetails;
